import React, { useState, memo } from 'react';

interface CreditCardImageProps {
  imageName?: string | null;
  alt: string;
  width?: number | string;
  height?: number | string;
  maxWidth?: number | string;
  maxHeight?: number | string;
  style?: React.CSSProperties;
  className?: string;
  onClick?: () => void;
}

const CreditCardImageFallback: React.FC<{
  width?: number | string;
  height?: number | string;
  maxWidth?: number | string;
  maxHeight?: number | string;
  style?: React.CSSProperties;
}> = memo(({ width, height, maxWidth, maxHeight, style = {} }) => {
  // Calculate the effective dimensions that match what an img element would render
  // Use consistent aspect ratio for all fallback images
  const FALLBACK_ASPECT_RATIO = 1.4; // Slightly less wide than typical credit cards for better visual balance

  const getEffectiveDimensions = () => {
    // Helper function to calculate height from width using consistent ratio
    const calculateHeightFromWidth = (widthValue: number | string) => {
      const widthNum =
        typeof widthValue === 'number'
          ? widthValue
          : parseInt(String(widthValue), 10) || 120;
      return Math.round(widthNum / FALLBACK_ASPECT_RATIO);
    };

    // Scenario 1: Auto height (CardCarousel, useCardModal)
    if (height === 'auto') {
      if (maxWidth && width === '100%') {
        // useCardModal case: 100% width, maxWidth constraint, auto height
        // For fallback div, use maxWidth as the actual width so it sizes correctly
        const effectiveWidth =
          typeof maxWidth === 'number'
            ? maxWidth
            : parseInt(String(maxWidth), 10) || 200;
        const calculatedHeight = calculateHeightFromWidth(effectiveWidth);

        return {
          width: effectiveWidth,
          height: calculatedHeight,
          maxWidth,
          maxHeight,
        };
      }

      // CardCarousel case: fixed width with auto height
      const calculatedHeight = calculateHeightFromWidth(width || 120);
      return {
        width,
        height: calculatedHeight,
        maxWidth,
        maxHeight,
      };
    }

    // Scenario 2: Percentage dimensions (MobileCardContainer "100%" with fixed container)
    if (width === '100%' || height === '100%') {
      return {
        width,
        height,
        maxWidth,
        maxHeight,
      };
    }

    // Scenario 3: Fixed width with maxHeight (CardButton, TableCardButton)
    if (width && maxHeight && !height) {
      // Calculate height using consistent ratio, but respect maxHeight constraint
      const calculatedHeight = calculateHeightFromWidth(width);
      const finalHeight = Math.min(
        calculatedHeight,
        typeof maxHeight === 'number'
          ? maxHeight
          : parseInt(String(maxHeight), 10)
      );

      return {
        width,
        height: finalHeight,
        maxWidth,
        maxHeight,
      };
    }

    // Scenario 4: Fixed width only (CreditCardSearchBar)
    if (width && !height && !maxHeight) {
      const calculatedHeight = calculateHeightFromWidth(width);
      return {
        width,
        height: calculatedHeight,
        maxWidth,
        maxHeight,
      };
    }

    // Scenario 5: Default fallback
    const defaultWidth = typeof width === 'number' ? width : 120;
    return {
      width: width || defaultWidth,
      height: height || calculateHeightFromWidth(defaultWidth),
      maxWidth,
      maxHeight,
    };
  };

  const effectiveDimensions = getEffectiveDimensions();

  return (
    <div
      style={{
        ...effectiveDimensions,
        backgroundColor: 'rgb(237, 239, 240)',
        border: '2px dashed rgb(164, 182, 195)',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        color: '#6488A3',
        fontSize: '12px',
        fontFamily: 'Poppins, sans-serif',
        boxSizing: 'border-box',
        objectFit: 'contain', // Match the img element's objectFit
        ...style,
      }}
    >
      <div style={{ fontSize: '16px', marginBottom: '2px' }}>💳</div>
      <div style={{ fontSize: '10px', textAlign: 'center', fontWeight: 400 }}>
        No image
      </div>
    </div>
  );
});

CreditCardImageFallback.displayName = 'CreditCardImageFallback';

export const CreditCardImage: React.FC<CreditCardImageProps> = memo(
  ({
    imageName,
    alt,
    width = 120,
    height,
    maxWidth,
    maxHeight,
    style = {},
    className,
    onClick,
  }) => {
    const [hasError, setHasError] = useState(false);

    // Show fallback if no imageName or if image failed to load
    if (!imageName || hasError) {
      return (
        <CreditCardImageFallback
          width={width}
          height={height}
          style={{
            maxWidth,
            maxHeight,
            objectFit: 'contain',
            ...style,
          }}
        />
      );
    }

    return (
      <img
        src={`/cards/${imageName}`}
        alt={alt}
        width={width}
        height={height}
        style={{
          maxWidth,
          maxHeight,
          objectFit: 'contain',
          ...style,
        }}
        className={className}
        onClick={onClick}
        onError={() => {
          setHasError(true);
        }}
      />
    );
  }
);

CreditCardImage.displayName = 'CreditCardImage';
